import { getConfig } from '@belong/configs/next/config';
import { IRouteDefinition, IRouteMatch } from '@belong/types';
import { logger } from '../logger';
import type { NewRelicEventType } from './types';

/**
 * NewRelic logging utils
 * Note: Ensure the newrelic package is ONLY imported on the server to avoid
 *       NextJS build errors
 */

/**
 * This is a duplication of
 * import { matchRoute } from '@belong/url-utils', to avoid circular dependency
 */
const matchRoute = (path = '', routes: IRouteDefinition[]): IRouteMatch | null => {
  // remove hash and search before comparisons
  const cleanedPath = path.replace(/[?#].*$/, '');
  const route = routes.find(({ pattern }) => !!pattern.match(cleanedPath));
  if (!route) {
    return null;
  }

  const match = route.pattern.match(cleanedPath);
  return {
    pathname: route.path.replace('*', match._),
    spec: route.path,
    match
  };
};

/**
 * This is a duplication of
 * import { withBasePath } from '@belong/url-utils', to avoid circular dependency
 */
const withBasePath = (path?: string): string | undefined => {
  if (!path) {
    return;
  }

  const { basePath = '' } = getConfig().publicRuntimeConfig;
  if (!basePath || path.startsWith(basePath)) {
    return path;
  }

  return `${basePath}/${path}`.replaceAll('//', '/').replace(/\/$/, '');
};

// Create script injector for browser activity monitoring (only used in _document)
export function getClientMonitorScript(): string {
  const { analytic } = getConfig().publicRuntimeConfig;
  if (analytic?.newRelicEnabled && typeof window === 'undefined') {
    // eslint-disable-next-line global-require
    return require('newrelic').getBrowserTimingHeader({
      hasToRemoveScriptWrapper: true
    });
  }

  return '';
}

// Send an error to NewRelic
export function logError(error: Error): void {
  if (!error) {
    return;
  }

  if (typeof window === 'undefined') {
    try {
      // eslint-disable-next-line global-require
      require('newrelic').noticeError(error);
    } catch (e) {
      //
    }
  } else {
    (window as any).newrelic?.noticeError(error);
  }
}

export function sanitizePath(nextUrl: string, appRoutes: IRouteDefinition[]): string {
  const [oldPath, oldParams = ''] = nextUrl.split('?');
  const newPath = matchRoute(oldPath, appRoutes)?.pathname.replace(/\/:([^/]+)/g, '/*') || oldPath;

  const newParams = Array.from(new URLSearchParams(oldParams).entries())
    .reduce((acc, [key, val]) => {
      return `${acc}${key}=${sanitizePath(decodeURIComponent(val), appRoutes)}&`;
    }, '?')
    .slice(0, -1);

  return `${newPath}${newParams}`;
}

// NewRelic not picking up NextJS client route changes so need to send manually
// https://docs.newrelic.com/docs/browser/single-page-app-monitoring/troubleshooting/missing-route-changes-spa-agent/
export function logClientRouteChange(nextUrl: string, appRoutes: IRouteDefinition[]): void {
  const interaction = (window as any).newrelic?.interaction();
  if (interaction) {
    interaction.setName(sanitizePath(nextUrl, appRoutes));
    interaction.save();
  }
}

/** Send a browser PageAction with a tag used to group related actions. */
export function sendCustomNRPageAction(tag: string, action: string, attributes?: Record<string, any>): void {
  if (typeof window !== 'undefined') {
    (window as any).newrelic?.addPageAction?.(action, {
      ...attributes,
      tag
    });
  }
}

/** Send the same typed newrelic custom event from either the client or server. */
export function sendCustomNREvent(eventType: NewRelicEventType, attributes: Record<string, any>): void {
  const { resolvedUrl: suppliedResolvedUrl, ...eventAttributes } = attributes;

  if (typeof window === 'undefined') {
    try {
      // Ensure the resolved URL includes the base path for server-side events
      const resolvedUrl = withBasePath(suppliedResolvedUrl?.split('?')[0]);
      // eslint-disable-next-line global-require
      require('newrelic').recordCustomEvent(eventType, {
        ...eventAttributes,
        ...(resolvedUrl ? { resolvedUrl } : {}),
        callerOrigin: 'server'
      });
    } catch (e) {
      logger.error(
        `[sendCustomNREvent] Failed to send ${eventType} on server, with ${JSON.stringify(attributes)}, error: ${e}`
      );
    }
  } else {
    const newRelic = (window as any).newrelic;
    // Browsers always have a resolved URL based on the current window location
    const resolvedUrl = window.location?.pathname;
    newRelic?.recordCustomEvent?.(eventType, {
      ...eventAttributes,
      ...(resolvedUrl ? { resolvedUrl } : {}),
      callerOrigin: 'client'
    });
  }
}
